import { Fragment, useEffect, useState } from 'react';
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from '@headlessui/react';

import {
  clearAnalyticsConsentStore,
  clearEntryAcknowledgedStore,
  clearProgressStore,
  clearTrailStore,
  getAnalyticsOptedOut,
} from '../../utils/localStorage';
import {
  ExclamationTriangleIcon,
  XMarkIcon,
  ArrowPathIcon,
  ArrowsRightLeftIcon,
  QuestionMarkCircleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/20/solid';
import HelpScreen from '../HelpScreen/HelpScreen';
import Logo from '../Loading/Logo';
import EmailLink from '../GDPR/EmailLink';
import useConsentChoice from '../GDPR/useConsentChoice';

export interface Props {
  dialog: string | null;
  close: () => void;
}

export interface DialogItem {
  title: string;
  icon: React.ReactNode;
  body: React.ReactNode;
  actionButton: React.ReactNode | null;
  closeButtonText: string;
}

function TrailMenuDialog({ dialog, close }: Props) {
  const chooseConsent = useConsentChoice();
  const [analyticsOptedOut, setAnalyticsOptedOutState] = useState(() =>
    getAnalyticsOptedOut()
  );
  const [saveError, setSaveError] = useState(false);

  // This component mounts once (it lives for the lifetime of the Trail
  // screen; `dialog` just toggles which panel is visible) — so re-sync the
  // toggle from storage whenever the privacy panel is opened, in case the
  // preference changed elsewhere (e.g. the entry screen) since last render.
  useEffect(() => {
    if (dialog === 'privacyChoices') {
      setAnalyticsOptedOutState(getAnalyticsOptedOut());
      setSaveError(false);
    }
  }, [dialog]);

  const handleToggleOptOut = (nextOptedOut: boolean) => {
    setAnalyticsOptedOutState(nextOptedOut);
    const persisted = chooseConsent(nextOptedOut);
    setSaveError(!persisted);
  };

  const dialogItems: { [key: string]: DialogItem } = {
    help: {
      title: 'Help',
      icon: (
        <QuestionMarkCircleIcon
          className="text-mt-danger h-6 w-6"
          aria-hidden="true"
        />
      ),
      body: <HelpScreen />,
      actionButton: null,
      closeButtonText: 'close',
    },
    about: {
      title: 'About',
      icon: (
        <div className="mx-auto flex h-16 w-16 items-center justify-center">
          <Logo baseSize={1.5} />
        </div>
      ),
      body: (
        <div className="mt-2">
          <p className="text-mt-ink-muted text-sm">
            &copy; {new Date().getFullYear()} Magical Trails
          </p>
          <p className="text-mt-ink-muted text-sm">
            Send feedback or suggestions to:{' '}
            <EmailLink className="text-mt-ink" />
          </p>

          <p className="text-sm">
            <a href="/privacy-policy" target="_blank" className="text-mt-ink">
              Privacy Policy
            </a>
          </p>
        </div>
      ),
      actionButton: null,
      closeButtonText: 'close',
    },
    privacyChoices: {
      title: 'Privacy choices',
      icon: (
        <ShieldCheckIcon
          className="text-mt-danger h-6 w-6"
          aria-hidden="true"
        />
      ),
      body: (
        <div className="mt-2 space-y-2">
          <p className="text-mt-ink-muted text-sm">
            Anonymous usage stats are currently{' '}
            <strong>{analyticsOptedOut ? 'OFF' : 'ON'}</strong>. We collect
            anonymous, cookieless usage statistics (like which trail points
            you've visited) to help improve the trails — not linked to your
            account or a persistent identifier. It never affects whether you can
            play — see our{' '}
            <a
              href="/privacy-policy"
              target="_blank"
              rel="noreferrer"
              className="text-mt-ink underline"
            >
              Privacy Policy
            </a>
            .
          </p>
          <label
            htmlFor="menu-analytics-opt-out"
            className="flex items-start gap-2"
          >
            <input
              id="menu-analytics-opt-out"
              type="checkbox"
              checked={analyticsOptedOut}
              onChange={(event) => handleToggleOptOut(event.target.checked)}
              className="border-mt-border text-mt-primary focus:ring-mt-primary mt-0.5 h-4 w-4 rounded"
            />
            <span className="text-mt-ink-soft text-sm">
              Turn off anonymous usage stats
            </span>
          </label>
          {saveError && (
            <p className="text-mt-danger text-xs">
              We couldn't save this preference on this device — it'll still
              apply for this visit, but you may need to set it again next time.
            </p>
          )}
        </div>
      ),
      actionButton: null,
      closeButtonText: 'Done',
    },
    resetStoryProgress: {
      title: 'Are you sure you want to reset story progress?',
      icon: (
        <ArrowPathIcon className="text-mt-danger h-6 w-6" aria-hidden="true" />
      ),
      body: (
        <div className="mt-2">
          <p className="text-mt-ink-muted text-sm">Reset Story Progress</p>
        </div>
      ),
      actionButton: (
        <button
          type="button"
          className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-red-500 sm:ml-3 sm:w-auto"
          onClick={() => {
            // Add reset progress
            close();
          }}
        >
          Reset Progress
        </button>
      ),
      closeButtonText: 'close',
    },
    switchStory: {
      title: 'switchStory',
      icon: (
        <ArrowsRightLeftIcon
          className="text-mt-danger h-6 w-6"
          aria-hidden="true"
        />
      ),
      body: (
        <div className="mt-2">
          <p className="text-mt-ink-muted text-sm">Switch Story</p>
        </div>
      ),
      actionButton: null,
      closeButtonText: 'close',
    },
    deleteAllSavedData: {
      title: 'Are you sure you want to delete all saved data?',
      icon: (
        <div className="bg-mt-danger-soft mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10">
          <ExclamationTriangleIcon
            className="text-mt-danger h-6 w-6"
            aria-hidden="true"
          />
        </div>
      ),
      body: (
        <div className="mt-2">
          <p className="text-mt-ink-muted text-sm">
            Are you sure you want to delete all saved data from your device?
            This action cannot be undone.
          </p>
        </div>
      ),
      actionButton: (
        <button
          type="button"
          className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-red-500 sm:ml-3 sm:w-auto"
          onClick={() => {
            close();
            // Clear all four localStorage keys this app writes — previously
            // only the trail/progress stores were cleared, leaving the
            // consent + entry-acknowledgement records behind ("delete all
            // saved data" wasn't). Wrapped in try/catch so a single
            // removeItem throwing (e.g. storage disabled) can't abort the
            // reload and strand the visitor on a stale screen.
            try {
              clearTrailStore();
              clearProgressStore();
              clearAnalyticsConsentStore();
              clearEntryAcknowledgedStore();
            } catch (error) {
              console.warn('Could not fully clear saved data:', error);
            }
            location.reload();
          }}
        >
          Delete All Data
        </button>
      ),
      closeButtonText: 'Cancel',
    },
  };
  return (
    <Transition
      show={!!dialog && dialogItems[dialog] !== undefined}
      as={Fragment}
    >
      <Dialog as="div" className="relative z-10" onClose={close}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel className="bg-mt-surface relative transform overflow-hidden rounded-lg px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
                  <button
                    type="button"
                    className="bg-mt-surface text-mt-ink-faint hover:text-mt-ink-muted focus:ring-mt-primary rounded-md focus:ring-2 focus:ring-offset-2 focus:outline-hidden"
                    onClick={() => close()}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div className="sm:flex sm:items-start">
                  {!!dialog && dialogItems[dialog].icon}
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <DialogTitle
                      as="h3"
                      className="text-mt-ink text-base leading-6 font-semibold"
                    >
                      {!!dialog && dialogItems[dialog].title}
                    </DialogTitle>
                    {!!dialog && dialogItems[dialog].body}
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  {!!dialog && dialogItems[dialog].actionButton}
                  <button
                    type="button"
                    className="bg-mt-surface text-mt-ink ring-mt-border hover:bg-mt-surface-2 mt-3 inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold shadow-xs ring-1 ring-inset sm:mt-0 sm:w-auto"
                    onClick={() => close()}
                  >
                    {!!dialog && dialogItems[dialog].closeButtonText}
                  </button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export default TrailMenuDialog;
